import { useMemo } from "react";
import { BANNER_PATTERNS, THEME_COLORS } from "../../../shared/profilePresets";
import { cn } from "@/lib/utils";

interface ProfileBannerProps {
  themeColor: string;
  bannerPatternId?: string | null;
  streakActive?: boolean;
}

const ROWS = 9;
const COLS = 16;

export function ProfileBanner({
  themeColor,
  bannerPatternId,
  streakActive = false,
}: ProfileBannerProps) {
  const colorDef = THEME_COLORS[themeColor] || THEME_COLORS.blue;
  const pattern = bannerPatternId ? BANNER_PATTERNS[bannerPatternId] : null;

  const rows = useMemo(() => {
    if (!pattern) return null;
    return Array.from({ length: ROWS }, (_, r) => ({
      stagger: r % 2 !== 0,
      emojis: Array.from({ length: COLS }, () => pattern.emoji),
    }));
  }, [pattern]);

  return (
    <div
      data-testid="profile-banner"
      className={cn(
        "relative w-full h-20 md:h-28 lg:h-32 rounded-t-xl overflow-hidden transition-all duration-300",
        colorDef.bgClass,
        "profile-banner--base",
        streakActive &&
          "profile-banner--streak-active ring-2 ring-yellow-400/50 shadow-lg shadow-yellow-400/20"
      )}
    >
      {rows && (
        <div
          aria-hidden="true"
          className="pointer-events-none select-none absolute"
          style={{ inset: "-70%" }}
        >
          <div
            style={{
              transform: "rotate(-20deg)",
              display: "flex",
              flexDirection: "column",
              gap: "1.1rem",
              width: "100%",
              height: "100%",
              justifyContent: "center",
            }}
          >
            {rows.map((row, ri) => (
              <div
                key={ri}
                style={{
                  display: "flex",
                  gap: "1.4rem",
                  marginLeft: row.stagger ? "2.2rem" : "0",
                }}
              >
                {row.emojis.map((emoji, ci) => (
                  <span
                    key={ci}
                    style={{ fontSize: "1rem", lineHeight: 1, flexShrink: 0 }}
                  >
                    {emoji}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
